import { useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { BottomTabBar } from './_components/bottom-tab-bar';
import { RightPane } from './_components/right-pane';
import { Sidebar } from './_components/sidebar';
import { Topbar } from './_components/topbar';

import { useProtectedRoute } from '@/shared/auth/use-protected-route';
import { Sheet } from '@/shared/components/ui/sheet';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  useIsDesktop,
  useIsIntermediate,
  useIsMobile,
  useIsTablet,
} from '@/shared/hooks/use-media-query';
import { useScrollToTop } from '@/shared/hooks/use-scroll-to-top';

/**
 * Shell autenticado. Adapta a tres faixas de viewport (CLAUDE.md):
 *  - Mobile (<900): SÓ bottom-tab + topbar enxuta (título + busca + sino). SEM
 *    drawer/hamburger — os destinos secundários (Notificações, Configurações,
 *    LGPD, Ajuda, Sair) vivem no hub da aba Perfil. Menu mobile feito a partir
 *    da OPÇÃO B "Bottom tabs puro + hub no Perfil" (ver
 *    __mocks__/mobile-nav/option-b.tsx) — removeu a Sidebar-desktop-em-drawer,
 *    que era redundante com o bottom-tab.
 *  - Tablet (900-1199): Sidebar fixa colapsada (72px), sem right-pane.
 *  - Intermediario (1200-1439): Sidebar fixa full + right-pane em drawer (toggle).
 *  - Desktop (>=1440): triple-pane (sidebar + content + right-pane fixo).
 *
 * Auth-guard via `useProtectedRoute()` — redireciona nao-autenticados para
 * /login. Enquanto a sessao restaura no boot (`isLoading` no auth store),
 * renderiza um skeleton de tela inteira para evitar flash do shell.
 */
export function AppLayout() {
  const { isLoading } = useProtectedRoute();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isIntermediate = useIsIntermediate();
  const isDesktop = useIsDesktop();

  const [rightPaneOpen, setRightPaneOpen] = useState(false);

  // O `<main>` (overflow-y-auto) e o container rolavel real do shell — nao o
  // window. O React Router nao reseta scroll de container interno, entao a
  // cada troca de rota voltamos o `<main>` ao topo via este hook.
  const mainRef = useRef<HTMLElement>(null);
  useScrollToTop(mainRef);

  if (isLoading) return <AppLayoutSkeleton />;

  return (
    <div
      className="flex min-h-screen bg-canvas text-on-surface"
      data-testid="app-layout"
      data-viewport={
        isDesktop ? 'desktop' : isIntermediate ? 'intermediate' : isTablet ? 'tablet' : 'mobile'
      }
    >
      {!isMobile ? <Sidebar collapsed={isTablet} /> : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar {...(isIntermediate ? { onOpenRightPane: () => setRightPaneOpen(true) } : {})} />
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto px-4 pb-24 pt-4 md:px-6 md:pb-8 md:pt-6 lg:px-8"
          data-testid="app-main"
        >
          <div className="mx-auto w-full max-w-[1240px]">
            <Outlet />
          </div>
        </main>
        {isMobile ? <BottomTabBar /> : null}
      </div>

      {isDesktop ? <RightPane /> : null}

      {/* OPÇÃO B: nada de drawer/hamburger no mobile — secundários no hub do
          Perfil. O right-pane (intermediário) continua em drawer. */}
      {isIntermediate ? (
        <Sheet
          open={rightPaneOpen}
          onOpenChange={setRightPaneOpen}
          title="Atividade recente"
          testID="right-pane-drawer"
        >
          <RightPane embedded testID="app-right-pane-drawer" />
        </Sheet>
      ) : null}
    </div>
  );
}

/**
 * Skeleton minimo do shell autenticado, usado enquanto auth.store esta em
 * `isLoading: true` (boot). NAO usar `<Skeleton/>` para todo o shell — so
 * uma estrutura grossa que evita CLS e da feedback de carregamento.
 */
function AppLayoutSkeleton() {
  return (
    <div
      className="flex min-h-screen bg-canvas"
      data-testid="app-layout-skeleton"
      role="status"
      aria-label="Carregando aplicação"
    >
      <div className="hidden h-screen w-[72px] flex-shrink-0 bg-surface-low md:block lg:w-[240px]" />
      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        <Skeleton height={48} className="w-full max-w-[320px]" />
        <Skeleton height={140} className="w-full" />
        <Skeleton height={240} className="w-full" />
      </div>
    </div>
  );
}
